import { useApolloClient } from '@apollo/client/react';
import { App, type ModalFuncProps } from 'antd';
import { useEffect, useEffectEvent, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router';
import { useAppStore } from '../hooks/store/useAppStore';
import { contextIdOf, isAisLinkMessage } from './aisLinkMessage';
import { leaveLinkSource, tokenOf } from './aisLinkToken';
import { AIS_LINK_PATH_PREFIX } from './aisSignin';
import { discardAisTicket } from './discardAisTicket';
import { openRedeemed } from './openRedeemed';
import { AisRedeemError, redeemAisContext } from './redeemAisContext';
import { aisOriginsOf } from './useAisIdentity';

const TITLE = 'Laboranforderung aus dem AIS';
const RELOAD_TEXTS: Record<number, string> = {
  404: 'Der Aufruf aus Ihrem AIS ist hier nicht mehr gültig. Soll labcomplete® die Anforderung neu laden?',
  409: 'Diese Anforderung wurde für einen anderen Benutzer geöffnet. Soll labcomplete® sie neu laden und Sie dabei neu anmelden?',
};
const FAILED_TEXT = 'Die Anforderung konnte nicht geöffnet werden. Bitte erneut aus dem AIS öffnen.';

const linkTokenOf = (url: string): string =>
  url.startsWith(`${window.location.origin}${AIS_LINK_PATH_PREFIX}`) ? tokenOf(new URL(url).hash) : '';

const openThroughLogin = (url: string, origin: string, token: string) => {
  leaveLinkSource(origin, token);
  window.location.assign(url);
};

// The AIS hands its links into the open tab; without the reply it opens them itself
export const AisLinkListener = () => {
  const { user } = useAuth();
  const { modal, message } = App.useApp();
  const navigate = useNavigate();
  const client = useApolloClient();
  const setTempAisImportResult = useAppStore(state => state.setTempAisImportResult);
  const modalOpen = useRef(false);
  const handledTokens = useRef(new Set<string>());

  const show = (open: (props: ModalFuncProps) => void, props: ModalFuncProps) => {
    if (modalOpen.current) return;
    modalOpen.current = true;
    open({
      title: TITLE,
      ...props,
      afterClose: () => {
        modalOpen.current = false;
      },
    });
  };

  // a reload logs in with the ticket, so only a link that opened in place burns it
  const showFailure = (url: string, origin: string, token: string, e: unknown) => {
    const reloadText = e instanceof AisRedeemError ? RELOAD_TEXTS[e.status] : undefined;
    if (reloadText) {
      show(modal.confirm, {
        content: reloadText,
        okText: 'Neu laden',
        cancelText: 'Abbrechen',
        onOk: () => openThroughLogin(url, origin, token),
      });
    } else {
      // the ticket may still be good, and the AIS opens nothing itself once the tab accepted the link
      handledTokens.current.delete(token);
      show(modal.error, { content: FAILED_TEXT, okText: 'OK' });
    }
  };

  const onMessage = useEffectEvent((event: MessageEvent) => {
    if (!isAisLinkMessage(event.data) || !aisOriginsOf(user).includes(event.origin)) return;
    const { url } = event.data;
    const token = linkTokenOf(url);
    if (!token) return;
    (event.source as Window | null)?.postMessage({ type: 'labcomplete:link-accepted' }, event.origin);
    // a repeat would find the ticket used and offer a reload that ends on the expired page
    if (handledTokens.current.has(token)) return;
    handledTokens.current.add(token);

    const contextId = contextIdOf(token);
    if (!contextId) {
      openThroughLogin(url, event.origin, token);
      return;
    }
    redeemAisContext(user?.access_token ?? '', contextId)
      .then(result => {
        discardAisTicket(token);
        return openRedeemed(result, client, navigate, setTempAisImportResult, modal, message, user);
      })
      .catch(e => showFailure(url, event.origin, token, e));
  });

  useEffect(() => {
    const listener = (event: MessageEvent) => onMessage(event);
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  return null;
};
